// Texto 29 (item 4e, REGRAS 3.5): os blocos de Texto livre, Lista e Desenho deslizam SOZINHOS (não a caixa inteira).
// Para a direita = excluir o bloco (pergunta se tiver conteúdo, com "Desfazer"); para a esquerda = duplicar o bloco.
// Não desliza com o bloco em edição (cursor piscando) nem com um item sendo segurado; no Desenho, só pela faixa de cima
// (quem decide é "pode()"). O toque que começa aqui é do bloco: a caixa de fora não desliza (lib/gestos).
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Animated, PanResponder, Text, View } from 'react-native';
import { C } from './theme';
import { t } from '../lib/i18n';
import { marcarToqueInterno, paraOLado } from '../lib/gestos';
import { segurandoItem, useAreaRolagem } from './Arrastar';

export function BlocoDeslizar({ pode, onExcluir, onDuplicar, children }: {
  /** lido no começo de cada deslizar: false = não desliza (em edição; Desenho fora da faixa de cima) */
  pode: () => boolean;
  onExcluir: () => void; onDuplicar: () => void; children: ReactNode;
}) {
  const x = useRef(new Animated.Value(0)).current;
  const larg = useRef(320);
  const [lado, setLado] = useState<0 | 1 | -1>(0);
  const R = useRef({ pode, onExcluir, onDuplicar }); R.current = { pode, onExcluir, onDuplicar };
  const area = useAreaRolagem();
  const areaRef = useRef(area); areaRef.current = area;
  const travar = (on: boolean) => areaRef.current?.setRolagem(!on);
  const voltar = () => Animated.spring(x, { toValue: 0, useNativeDriver: false, bounciness: 6 }).start(() => setLado(0));
  const pan = useMemo(() => {
    const quer = (_: unknown, g: { dx: number; dy: number }) => paraOLado(g) && !segurandoItem() && R.current.pode();
    return PanResponder.create({
      onMoveShouldSetPanResponderCapture: quer,
      onMoveShouldSetPanResponder: quer,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => travar(true),   // REGRAS 0.5: enquanto desliza, a tela não rola
      onPanResponderMove: (_, g) => { x.setValue(g.dx); setLado(g.dx > 0 ? 1 : g.dx < 0 ? -1 : 0); },
      onPanResponderRelease: (_, g) => {
        travar(false);
        const lim = Math.min(120, larg.current * 0.35);
        voltar();
        if (g.dx > lim) R.current.onExcluir();
        else if (g.dx < -lim) R.current.onDuplicar();
      },
      onPanResponderTerminate: () => { travar(false); voltar(); },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const opDir = x.interpolate({ inputRange: [0, 40], outputRange: [0, 1], extrapolate: 'clamp' });
  const opEsq = x.interpolate({ inputRange: [-40, 0], outputRange: [1, 0], extrapolate: 'clamp' });
  return (
    <View onLayout={e => { larg.current = e.nativeEvent.layout.width; }} onStartShouldSetResponderCapture={marcarToqueInterno}>
      {lado !== 0 && (
        <View pointerEvents="none" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, borderRadius: 10, overflow: 'hidden' }}>
          <Animated.View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: C.danger, opacity: opDir, justifyContent: 'center', paddingLeft: 14 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>🗑 {t('Excluir')}</Text>
          </Animated.View>
          <Animated.View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: '#6366F1', opacity: opEsq, justifyContent: 'center', alignItems: 'flex-end', paddingRight: 14 }}>
            <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>⧉ {t('Duplicar')}</Text>
          </Animated.View>
        </View>
      )}
      <Animated.View {...pan.panHandlers} style={{ transform: [{ translateX: x }] }}>{children}</Animated.View>
    </View>
  );
}
